import { Shuffle, X, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { firstName } from '../../lib/play';

// Strongest with weakest, so every pair lands on a similar combined strength.
export function suggestPairs(ids, ratingOf) {
  const ranked = [...ids].sort((x, y) => (ratingOf[y]?.rating || 0) - (ratingOf[x]?.rating || 0));
  const pairs = [];
  let lo = 0;
  let hi = ranked.length - 1;
  while (hi - lo >= 1) {
    pairs.push([ranked[lo], ranked[hi]]);
    lo += 1;
    hi -= 1;
  }
  return pairs;
}

const TONES = [
  'bg-amber-500/15 border-amber-500/50',
  'bg-sky-500/15 border-sky-500/50',
  'bg-emerald-500/15 border-emerald-500/50',
  'bg-violet-500/15 border-violet-500/50',
  'bg-rose-500/15 border-rose-500/50',
  'bg-teal-500/15 border-teal-500/50',
];

/**
 * Fixed-partner round robin editor. Tap two unpaired players to pair them,
 * tap a pair to break it. The app can suggest balanced pairs too.
 */
export default function PairsEditor({ pairs, unpaired, ratingOf, onChange, onReSuggest, canEdit, picking, onPick }) {
  const nm = (id) => firstName(ratingOf[id]?.name || 'Guest');
  const strength = (p) => Math.round(p.reduce((s, id) => s + (ratingOf[id]?.rating || 0), 0) / p.length);
  const breakPair = (i) => canEdit && onChange(pairs.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-2xl border p-3 space-y-3" data-testid="pairs-editor">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold">Fixed pairs</p>
          <p className="text-xs text-muted-foreground">
            Every pair plays every other pair once. Tap two names below to pair them.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs shrink-0" onClick={onReSuggest}
            data-testid="pairs-auto-balance">
            <Shuffle className="w-3.5 h-3.5 mr-1" /> Suggest
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        {pairs.map((p, i) => (
          <button key={p.join('|')} type="button" onClick={() => breakPair(i)} data-testid={`pair-row-${i}`}
            className={`w-full flex items-center justify-between gap-2 h-10 px-3 rounded-xl border text-xs font-semibold transition-colors ${TONES[i % TONES.length]}`}>
            <span className="inline-flex items-center gap-1.5 truncate">
              <Link2 className="w-3.5 h-3.5 opacity-60 shrink-0" />
              {p.map(nm).join('  &  ')}
            </span>
            <span className="inline-flex items-center gap-2 shrink-0">
              <span className="tabular-nums opacity-70">{strength(p)} avg</span>
              {canEdit && <X className="w-3.5 h-3.5 opacity-60" />}
            </span>
          </button>
        ))}
        {pairs.length === 0 && (
          <p className="text-xs text-muted-foreground">No pairs yet — tap Suggest, or pick two names below.</p>
        )}
      </div>

      {unpaired.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] mb-1.5 text-muted-foreground">
            Unpaired ({unpaired.length})
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="pairs-unpaired">
            {unpaired.map((id) => (
              <button key={id} type="button" onClick={() => canEdit && onPick(id)} data-testid={`unpaired-chip-${id}`}
                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-full border text-xs font-semibold transition-colors ${
                  picking === id ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                }`}>
                {nm(id)}
                <span className="tabular-nums opacity-70">{ratingOf[id]?.rating}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {pairs.length < 2 && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="pairs-too-few">
          Need at least 2 pairs for a round robin.
        </p>
      )}
      {unpaired.length > 0 && pairs.length >= 2 && (
        <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="pairs-leftover-warning">
          {unpaired.length} player{unpaired.length > 1 ? 's' : ''} without a partner will sit out the whole round robin.
        </p>
      )}
    </div>
  );
}
